import { weeklyWorkReportColors } from '../../../utils/dataStatus';

/** @typedef {keyof weeklyWorkReportColors["approver"]} ApprovalStatus */
/** @typedef {keyof weeklyWorkReportColors["imputation"]} ImputationStatus */
/** @typedef {keyof weeklyWorkReportColors["inspector"]} InspectionStatus */

/** @typedef {`approver.${ApprovalStatus}` | `imputation.${ImputationStatus}` | `inspector.${InspectionStatus}`} ActionTypes */
/** @typedef {{type: ActionTypes, payload?: number}} OptimisticUIActions */

/**
 * @typedef OptimisticUIState
 * @property {string} iconColor Color of the imputation type/object icon
 * @property {string} inspectionColor The background color of the lower part of the cell. Inspector's area
 * @property {number} quantity Defined by the approver/inspector in the reject/edit cell actions
 * @property {string} statusColors Tuple of the text color and cell background. e.g "#FFFFFF,#219E66". Approver's area
 */

/** @type {OptimisticUIState} */
export const optimisticInitialState = {
  iconColor: '',
  inspectionColor: '',
  quantity: undefined,
  statusColors: '',
  zeroAsDash: false // new flag to treat 0 as '--'
};

/**
 * @param {OptimisticUIState} state
 * @param {OptimisticUIActions} action
 */
export const optimisticUIReducer = (state, action) => {
  /** @type {['approver', ApprovalStatus] | ['imputation', ImputationStatus] | ['inspector', InspectionStatus]} */
  const [roleAction, executedAction] = action.type.split('.');

  const hasPayload = Object.prototype.hasOwnProperty.call(action, 'payload');

  if (roleAction === 'approver') {
    const pending = 'pending';
    const isReturned = executedAction === 'returned';
    const isFixed = executedAction === 'fixed';
    const isReturnedWithZero = (isReturned || isFixed) && hasPayload && +action.payload === 0;

    // manage the flag: returned with 0 -> true, fixed -> false, otherwise keep
    let zeroAsDashValue = state.zeroAsDash;

    if (isReturnedWithZero) {
      zeroAsDashValue = true;
    } else if (isFixed) {
      zeroAsDashValue = false;
    }

    return {
      ...state,
      // if payload is provided we use it (including 0); otherwise leave as is
      quantity: hasPayload ? +action.payload : state.quantity,
      // pending color only in the specific case
      statusColors: isReturnedWithZero
        ? weeklyWorkReportColors[roleAction][pending]
        : weeklyWorkReportColors[roleAction][executedAction],
      // manage the flag: returned with 0 -> true, fixed with 0 -> false, otherwise keep
      zeroAsDash: zeroAsDashValue
    };
  }

  if (roleAction === 'imputation') {
    return {
      ...state,
      iconColor: weeklyWorkReportColors[roleAction][executedAction]
    };
  }

  if (roleAction === 'inspector') {
    return {
      ...state,
      quantity: action?.payload ?? state.quantity,
      inspectionColor: weeklyWorkReportColors[roleAction][executedAction]
    };
  }

  return state;
};
